import { useQuery } from "@tanstack/react-query";
import { sum } from "es-toolkit";
import { useMemo } from "react";

import { getIsAuthenticated } from "@skilitics/designer-auth/state";

import { useContentHostId } from "../backend/index.ts";
import { useRootNodeData } from "../state/index.ts";
import type { UsageAnalyticsQueryCriteria } from "./types.ts";
import { useUsageAnalyticsDataContext } from "./UsageAnalyticsDataContext.ts";

export interface NodeVisitCounts {
  /** total number of visits including repeat visits */
  total: number;

  /** total visits excluding repeat visits in the same course attempt */
  unique: number;

  /** percentage of unique visits relative to the total number of unique visits in the module */
  uniquePercent: number;
}

/** response matches by response id */
export interface NodeResponseMatchCounts {
  countByResponseId: Map<number, number>;
  totalCount: number;
}

export interface NodeUsageAnalytics {
  /** response matches by response id */
  responseMatchCounts: NodeResponseMatchCounts;
  visitCounts: NodeVisitCounts;
}

const emptyNodeVisitCounts: NodeVisitCounts = { total: 0, unique: 0, uniquePercent: 0 };

export const useGetNodeUsageAnalytics = () => {
  const moduleId = useContentHostId();
  const moduleNode = useRootNodeData();
  const { api, dataAnalysisEnabled, dateRangeFilterEnabled, dateRange, moduleVersionFilterEnabled, moduleVersion } =
    useUsageAnalyticsDataContext();

  const criteriaList = useMemo(() => {
    const entries: [keyof UsageAnalyticsQueryCriteria, string][] = [];
    if (dateRangeFilterEnabled) {
      if (dateRange?.startDate) {
        entries.push(["startDate", dateRange.startDate.toUTCString()]);
      }
      // if (dateRange?.endDate) {
      //   entries.push(["endDate", dateRange.endDate.toUTCString()]);
      // }
    }
    if (moduleVersionFilterEnabled && moduleVersion) {
      entries.push(["moduleVersion", moduleVersion]);
    }
    return entries;
  }, [dateRange, dateRangeFilterEnabled, moduleVersion, moduleVersionFilterEnabled]);

  const criteriaFlat = useMemo((): string[] => criteriaList.flat(), [criteriaList]);
  const criteria = useMemo(() => Object.fromEntries(criteriaList), [criteriaList]);

  const { data: lookup } = useQuery({
    queryKey: ["module-usage", moduleId, ...criteriaFlat],
    queryFn: () => api.getModuleUsage(moduleId, criteria),
    enabled: dataAnalysisEnabled && getIsAuthenticated(),
    refetchOnWindowFocus: true,
    select: (moduleUsage) => {
      const { nodeUsages } = moduleUsage;
      if (nodeUsages.length === 0) return undefined; // no data yet

      const moduleUsages = nodeUsages.find(([id]) => id === moduleNode.id)?.[1];
      if (!moduleUsages) {
        console.warn("module node usage not found");
        return undefined;
      }

      const moduleUniqueVisits = moduleUsages.uniqueVisits;

      const learningUnitNodeUsages = nodeUsages.filter(([nodeId]) => nodeId !== moduleNode.id);

      const responseMatchCountsByNodeId = new Map(
        learningUnitNodeUsages.map(([nodeId, { responseMatches }]) => [nodeId, new Map(responseMatches)]),
      );

      const visitCountsByNodeId = new Map<number, NodeVisitCounts>(
        learningUnitNodeUsages.map(([nodeId, { uniqueVisits, totalVisits }]) => [
          nodeId,
          {
            total: totalVisits,
            unique: uniqueVisits,
            uniquePercent: Math.round((uniqueVisits / moduleUniqueVisits) * 100),
          },
        ]),
      );

      return { responseMatchCountsByNodeId, visitCountsByNodeId };
    },
    // retry: 3,
    // staleTime: 3 * 60 * 1000,
  });

  return useMemo(
    () =>
      (id: number): NodeUsageAnalytics | undefined => {
        if (!lookup) return undefined;

        const countByResponseId = lookup.responseMatchCountsByNodeId.get(id) ?? new Map<number, number>();

        return {
          responseMatchCounts: {
            countByResponseId,
            totalCount: sum([...countByResponseId.values()]),
          },
          visitCounts: lookup.visitCountsByNodeId.get(id) ?? emptyNodeVisitCounts,
        };
      },
    [lookup],
  );
};

export type GetNodeUsageAnalytics = ReturnType<typeof useGetNodeUsageAnalytics>;
